import * as THREE from 'three';
import { ComputerScreen } from './ComputerScreen';
import { sound } from '../audio/SoundEngine';

export interface InteractiveTarget {
  mesh: THREE.Object3D;
  type: 'screen' | 'cartridge_racer' | 'cartridge_ide' | 'cartridge_fps' | 'pc_tower' | 'keyboard';
  label: string;
}

export class Workstation {
  public group: THREE.Group = new THREE.Group();
  public interactiveObjects: THREE.Object3D[] = [];
  public targetMap: Map<THREE.Object3D, InteractiveTarget> = new Map();

  public computerScreen: ComputerScreen;
  public mainScreenMesh: THREE.Mesh;

  // Cartridge meshes
  public racerCartridgeMesh: THREE.Group;
  public ideCartridgeMesh: THREE.Group;
  public fpsCartridgeMesh: THREE.Group;
  private racerCartridgeBasePos: THREE.Vector3 = new THREE.Vector3(-1.4, 0.08, 0.4);
  private ideCartridgeBasePos: THREE.Vector3 = new THREE.Vector3(-0.9, 0.08, 0.5);
  private fpsCartridgeBasePos: THREE.Vector3 = new THREE.Vector3(-0.4, 0.08, 0.55);

  // Animated elements
  private fans: THREE.Mesh[] = [];
  private coolingFluid: THREE.Mesh[] = [];
  private leftAuxCanvas: HTMLCanvasElement;
  private leftAuxCtx: CanvasRenderingContext2D;
  private leftAuxTexture: THREE.CanvasTexture;
  private rightAuxCanvas: HTMLCanvasElement;
  private rightAuxCtx: CanvasRenderingContext2D;
  private rightAuxTexture: THREE.CanvasTexture;
  private time: number = 0;

  constructor(computerScreen: ComputerScreen) {
    this.computerScreen = computerScreen;

    // Build the Desk
    this.buildDesk();

    // Build Main Monitor
    this.mainScreenMesh = this.buildMainMonitor();

    // Build Auxiliary Side Monitors
    const { leftTexture, leftCtx, leftCanvas, rightTexture, rightCtx, rightCanvas } = this.buildAuxMonitors();
    this.leftAuxCanvas = leftCanvas;
    this.leftAuxCtx = leftCtx;
    this.leftAuxTexture = leftTexture;
    this.rightAuxCanvas = rightCanvas;
    this.rightAuxCtx = rightCtx;
    this.rightAuxTexture = rightTexture;

    // Build PC Tower
    this.buildPcTower();

    // Build Keyboard & Mouse
    this.buildKeyboardAndMouse();

    // Build Game Cartridges
    this.racerCartridgeMesh = this.buildCartridge('racer', '#ff0055', '#ff00aa', this.racerCartridgeBasePos);
    this.ideCartridgeMesh = this.buildCartridge('ide', '#00bbff', '#00fff2', this.ideCartridgeBasePos);
    this.fpsCartridgeMesh = this.buildCartridge('fps', '#e67300', '#ffaa00', this.fpsCartridgeBasePos);

    // Build Audio Synth Deck
    this.buildAudioDeck();

    // Build Glowing Conduit Cables
    this.buildCables();
  }

  private registerInteractive(mesh: THREE.Object3D, type: InteractiveTarget['type'], label: string) {
    this.interactiveObjects.push(mesh);
    this.targetMap.set(mesh, { mesh, type, label });
  }

  private buildDesk() {
    // Desk Top
    const deskGeo = new THREE.BoxGeometry(4.8, 0.12, 2.2);
    const deskMat = new THREE.MeshStandardMaterial({
      color: 0x111622,
      metalness: 0.85,
      roughness: 0.35,
    });
    const desk = new THREE.Mesh(deskGeo, deskMat);
    desk.position.set(0, 0, 0);
    desk.receiveShadow = true;
    this.group.add(desk);

    // Glowing Neon Edge Strip around desk
    const edgeGeo = new THREE.BoxGeometry(4.82, 0.02, 2.22);
    const edgeMat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
    });
    const edgeStrip = new THREE.Mesh(edgeGeo, edgeMat);
    edgeStrip.position.set(0, -0.05, 0);
    this.group.add(edgeStrip);

    // Desk Legs
    const legGeo = new THREE.BoxGeometry(0.12, 2.6, 0.12);
    const legMat = new THREE.MeshStandardMaterial({
      color: 0x0b0e14,
      metalness: 0.9,
      roughness: 0.4,
    });

    const legPositions = [
      [-2.2, -1.3, 0.9],
      [2.2, -1.3, 0.9],
      [-2.2, -1.3, -0.9],
      [2.2, -1.3, -0.9],
    ];

    legPositions.forEach(([x, y, z]) => {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(x, y, z);
      this.group.add(leg);
    });

    // Cross-brace bar
    const braceGeo = new THREE.BoxGeometry(4.4, 0.06, 0.06);
    const brace = new THREE.Mesh(braceGeo, legMat);
    brace.position.set(0, -1.8, -0.9);
    this.group.add(brace);
  }

  private buildMainMonitor(): THREE.Mesh {
    // Monitor Stand
    const standMat = new THREE.MeshStandardMaterial({
      color: 0x1a2130,
      metalness: 0.8,
      roughness: 0.3,
    });

    // Base plate
    const baseGeo = new THREE.CylinderGeometry(0.35, 0.4, 0.04, 24);
    const basePlate = new THREE.Mesh(baseGeo, standMat);
    basePlate.position.set(0, 0.08, -0.4);
    this.group.add(basePlate);

    // Articulated Arm
    const armGeo = new THREE.BoxGeometry(0.1, 0.85, 0.1);
    const arm = new THREE.Mesh(armGeo, standMat);
    arm.position.set(0, 0.5, -0.45);
    arm.rotation.x = 0.05;
    this.group.add(arm);

    // Monitor Outer Bezel Frame (Curved / Angled Cyber Widescreen)
    const bezelGeo = new THREE.BoxGeometry(2.3, 1.45, 0.12);
    const bezelMat = new THREE.MeshStandardMaterial({
      color: 0x090d16,
      metalness: 0.8,
      roughness: 0.3,
    });
    const bezel = new THREE.Mesh(bezelGeo, bezelMat);
    bezel.position.set(0, 1.05, -0.35);
    this.group.add(bezel);

    // Bezel Accent Neon Lines
    const topStripGeo = new THREE.BoxGeometry(2.26, 0.02, 0.02);
    const topStripMat = new THREE.MeshBasicMaterial({ color: 0x00fff2 });
    const topStrip = new THREE.Mesh(topStripGeo, topStripMat);
    topStrip.position.set(0, 1.76, -0.28);
    this.group.add(topStrip);

    // Screen Plane with Dynamic Canvas Texture
    const screenGeo = new THREE.PlaneGeometry(2.2, 1.35);
    const screenMesh = new THREE.Mesh(screenGeo, this.computerScreen.material);
    screenMesh.position.set(0, 1.05, -0.28);
    this.group.add(screenMesh);

    // Register screen as primary interactive target
    this.registerInteractive(screenMesh, 'screen', 'TOILLE CYBER-TERMINAL');

    // Soft glowing monitor point light illuminating keyboard & desk
    const monitorLight = new THREE.PointLight(0x00fff2, 1.8, 3.5);
    monitorLight.position.set(0, 1.0, 0.1);
    this.group.add(monitorLight);

    return screenMesh;
  }

  private buildAuxMonitors() {
    // LEFT AUX MONITOR (Matrix Code Stream)
    const auxMat = new THREE.MeshStandardMaterial({
      color: 0x0a0e18,
      metalness: 0.8,
      roughness: 0.4,
    });

    const leftBezel = new THREE.Mesh(new THREE.BoxGeometry(0.75, 1.25, 0.08), auxMat);
    leftBezel.position.set(-1.45, 0.95, -0.15);
    leftBezel.rotation.y = 0.42;
    this.group.add(leftBezel);

    // Left Screen Canvas
    const leftCanvas = document.createElement('canvas');
    leftCanvas.width = 256;
    leftCanvas.height = 512;
    const leftCtx = leftCanvas.getContext('2d')!;
    const leftTexture = new THREE.CanvasTexture(leftCanvas);
    const leftMaterial = new THREE.MeshBasicMaterial({ map: leftTexture });

    const leftScreen = new THREE.Mesh(new THREE.PlaneGeometry(0.68, 1.18), leftMaterial);
    leftScreen.position.set(-1.43, 0.95, -0.1);
    leftScreen.rotation.y = 0.42;
    this.group.add(leftScreen);

    // RIGHT AUX MONITOR (System Telemetry & Radar)
    const rightBezel = new THREE.Mesh(new THREE.BoxGeometry(0.75, 1.25, 0.08), auxMat);
    rightBezel.position.set(1.45, 0.95, -0.15);
    rightBezel.rotation.y = -0.42;
    this.group.add(rightBezel);

    // Right Screen Canvas
    const rightCanvas = document.createElement('canvas');
    rightCanvas.width = 256;
    rightCanvas.height = 512;
    const rightCtx = rightCanvas.getContext('2d')!;
    const rightTexture = new THREE.CanvasTexture(rightCanvas);
    const rightMaterial = new THREE.MeshBasicMaterial({ map: rightTexture });

    const rightScreen = new THREE.Mesh(new THREE.PlaneGeometry(0.68, 1.18), rightMaterial);
    rightScreen.position.set(1.43, 0.95, -0.1);
    rightScreen.rotation.y = -0.42;
    this.group.add(rightScreen);

    return {
      leftTexture,
      leftCtx,
      leftCanvas,
      rightTexture,
      rightCtx,
      rightCanvas,
    };
  }

  private buildPcTower() {
    const pcGroup = new THREE.Group();
    pcGroup.position.set(1.85, 0.65, 0.1);

    // Tower Outer Case
    const caseGeo = new THREE.BoxGeometry(0.55, 1.2, 0.95);
    const caseMat = new THREE.MeshStandardMaterial({
      color: 0x080b12,
      metalness: 0.9,
      roughness: 0.3,
    });
    const caseMesh = new THREE.Mesh(caseGeo, caseMat);
    pcGroup.add(caseMesh);

    // Glass Side Panel
    const glassGeo = new THREE.PlaneGeometry(0.9, 1.1);
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0x00fff2,
      transmission: 0.85,
      opacity: 0.6,
      transparent: true,
      roughness: 0.1,
      metalness: 0.1,
    });
    const glass = new THREE.Mesh(glassGeo, glassMat);
    glass.position.set(-0.28, 0, 0);
    glass.rotation.y = -Math.PI / 2;
    pcGroup.add(glass);

    // Glowing Motherboard & GPU Block inside
    const gpuGeo = new THREE.BoxGeometry(0.18, 0.12, 0.65);
    const gpuMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a24,
      emissive: 0x00fff2,
      emissiveIntensity: 0.4,
    });
    const gpu = new THREE.Mesh(gpuGeo, gpuMat);
    gpu.position.set(0.05, -0.1, 0.05);
    pcGroup.add(gpu);

    // Dual RGB Cooling Fans
    const fanGeo = new THREE.CylinderGeometry(0.14, 0.14, 0.02, 16);
    const fanMat = new THREE.MeshBasicMaterial({ color: 0xff007f });
    [-0.2, 0.25].forEach((y) => {
      const fan = new THREE.Mesh(fanGeo, fanMat);
      fan.position.set(-0.1, y, 0.46);
      fan.rotation.x = Math.PI / 2;
      pcGroup.add(fan);
      this.fans.push(fan);
    });

    // Cooling Liquid Tubes (Neon Glowing Cylinders)
    const tubeGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.75, 12);
    const tubeMat = new THREE.MeshBasicMaterial({ color: 0x00fff2 });
    const tube1 = new THREE.Mesh(tubeGeo, tubeMat);
    tube1.position.set(-0.05, 0.2, 0.1);
    tube1.rotation.z = 0.3;
    pcGroup.add(tube1);
    this.coolingFluid.push(tube1);

    // Tower Power LED
    const pwrGeo = new THREE.BoxGeometry(0.04, 0.04, 0.02);
    const pwrMat = new THREE.MeshBasicMaterial({ color: 0x39ff14 });
    const pwr = new THREE.Mesh(pwrGeo, pwrMat);
    pwr.position.set(-0.2, 0.55, 0.48);
    pcGroup.add(pwr);

    this.group.add(pcGroup);
    this.registerInteractive(caseMesh, 'pc_tower', 'TURBO RIG [CLICK TO BOOST]');
  }

  private buildKeyboardAndMouse() {
    // Keyboard Base
    const kbGeo = new THREE.BoxGeometry(1.1, 0.04, 0.4);
    const kbMat = new THREE.MeshStandardMaterial({
      color: 0x0d111a,
      metalness: 0.8,
      roughness: 0.4,
    });
    const kb = new THREE.Mesh(kbGeo, kbMat);
    kb.position.set(0, 0.08, 0.45);
    this.group.add(kb);

    // Backlit Key Matrix Plate
    const keyPlateGeo = new THREE.BoxGeometry(1.04, 0.03, 0.35);
    const keyPlateMat = new THREE.MeshStandardMaterial({
      color: 0x1e293b,
      emissive: 0x00fff2,
      emissiveIntensity: 0.5,
    });
    const keyPlate = new THREE.Mesh(keyPlateGeo, keyPlateMat);
    keyPlate.position.set(0, 0.1, 0.45);
    this.group.add(keyPlate);

    this.registerInteractive(kb, 'keyboard', 'CYBER MECHANICAL KEYBOARD');

    // Cyber Mouse
    const mouseGeo = new THREE.BoxGeometry(0.12, 0.05, 0.2);
    const mouseMat = new THREE.MeshStandardMaterial({
      color: 0x111622,
      metalness: 0.8,
      roughness: 0.3,
    });
    const mouse = new THREE.Mesh(mouseGeo, mouseMat);
    mouse.position.set(0.85, 0.08, 0.45);
    this.group.add(mouse);

    // Glowing Mouse Wheel
    const wheelGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.03, 12);
    const wheelMat = new THREE.MeshBasicMaterial({ color: 0xff007f });
    const wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.position.set(0.85, 0.11, 0.4);
    wheel.rotation.z = Math.PI / 2;
    this.group.add(wheel);
  }

  private buildCartridge(
    type: 'racer' | 'ide' | 'fps',
    colorHex: string,
    glowHex: string,
    pos: THREE.Vector3
  ): THREE.Group {
    const cartGroup = new THREE.Group();
    cartGroup.position.copy(pos);

    // Cartridge Housing
    const bodyGeo = new THREE.BoxGeometry(0.32, 0.06, 0.44);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(colorHex),
      metalness: 0.6,
      roughness: 0.3,
    });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    cartGroup.add(body);

    // Gold / Copper Contact Pins
    const pinGeo = new THREE.BoxGeometry(0.26, 0.02, 0.08);
    const pinMat = new THREE.MeshStandardMaterial({
      color: 0xffaa00,
      metalness: 0.95,
      roughness: 0.1,
    });
    const pins = new THREE.Mesh(pinGeo, pinMat);
    pins.position.set(0, 0, -0.22);
    cartGroup.add(pins);

    // Glowing Holographic Label Plate
    const labelCanvas = document.createElement('canvas');
    labelCanvas.width = 256;
    labelCanvas.height = 128;
    const lCtx = labelCanvas.getContext('2d')!;

    lCtx.fillStyle = '#0a0d18';
    lCtx.fillRect(0, 0, 256, 128);
    lCtx.strokeStyle = glowHex;
    lCtx.lineWidth = 6;
    lCtx.strokeRect(4, 4, 248, 120);

    lCtx.fillStyle = '#ffffff';
    lCtx.font = 'bold 28px "Orbitron", monospace';
    lCtx.textAlign = 'center';
    const titleText = type === 'racer' ? 'TOILLE RACER' : type === 'ide' ? 'TOILLE IDE' : 'TOILLE FPS';
    lCtx.fillText(titleText, 128, 55);

    lCtx.fillStyle = glowHex;
    lCtx.font = '18px "Share Tech Mono", monospace';
    const subText = type === 'racer' ? 'ARCADE CARTRIDGE' : type === 'ide' ? 'VIM WASM CORE' : 'DM-MORPHEUS UT';
    lCtx.fillText(subText, 128, 92);

    const labelTex = new THREE.CanvasTexture(labelCanvas);
    const labelMat = new THREE.MeshStandardMaterial({
      map: labelTex,
      emissive: new THREE.Color(glowHex),
      emissiveIntensity: 0.6,
    });
    const labelMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.28, 0.22), labelMat);
    labelMesh.position.set(0, 0.032, 0.02);
    labelMesh.rotation.x = -Math.PI / 2;
    cartGroup.add(labelMesh);

    this.group.add(cartGroup);

    // Register interactive
    const targetType = type === 'racer' ? 'cartridge_racer' : type === 'ide' ? 'cartridge_ide' : 'cartridge_fps';
    const labelHover =
      type === 'racer'
        ? 'RACER CARTRIDGE [CLICK TO INSERT]'
        : type === 'ide'
        ? 'IDE CARTRIDGE [CLICK TO INSERT]'
        : 'FPS CARTRIDGE [CLICK TO INSERT]';
    this.registerInteractive(body, targetType, labelHover);

    return cartGroup;
  }

  private buildAudioDeck() {
    const deckGeo = new THREE.BoxGeometry(0.75, 0.25, 0.45);
    const deckMat = new THREE.MeshStandardMaterial({
      color: 0x141a24,
      metalness: 0.8,
      roughness: 0.4,
    });
    const deck = new THREE.Mesh(deckGeo, deckMat);
    deck.position.set(-1.8, 0.16, 0.45);
    this.group.add(deck);

    // Glowing Knobs & VU Meter Bar
    const vuGeo = new THREE.BoxGeometry(0.65, 0.04, 0.02);
    const vuMat = new THREE.MeshBasicMaterial({ color: 0x39ff14 });
    const vu = new THREE.Mesh(vuGeo, vuMat);
    vu.position.set(-1.8, 0.22, 0.68);
    this.group.add(vu);
  }

  private buildCables() {
    // Neon fiber cables curving across the desk
    const curvePoints1 = [
      new THREE.Vector3(0, 0.5, -0.4),
      new THREE.Vector3(0.5, 0.08, -0.2),
      new THREE.Vector3(1.2, 0.08, 0.1),
      new THREE.Vector3(1.6, 0.2, 0.1),
    ];
    const curve1 = new THREE.CatmullRomCurve3(curvePoints1);
    const cableGeo1 = new THREE.TubeGeometry(curve1, 24, 0.015, 8, false);
    const cableMat1 = new THREE.MeshBasicMaterial({ color: 0x00fff2 });
    const cable1 = new THREE.Mesh(cableGeo1, cableMat1);
    this.group.add(cable1);

    const curvePoints2 = [
      new THREE.Vector3(0.2, 0.4, -0.4),
      new THREE.Vector3(-0.6, 0.08, -0.1),
      new THREE.Vector3(-1.4, 0.08, 0.2),
      new THREE.Vector3(-1.7, 0.16, 0.4),
    ];
    const curve2 = new THREE.CatmullRomCurve3(curvePoints2);
    const cableGeo2 = new THREE.TubeGeometry(curve2, 24, 0.015, 8, false);
    const cableMat2 = new THREE.MeshBasicMaterial({ color: 0xff007f });
    const cable2 = new THREE.Mesh(cableGeo2, cableMat2);
    this.group.add(cable2);
  }

  public animateCartridgeInsert(type: 'racer' | 'ide' | 'fps') {
    const targetGroup = type === 'racer' ? this.racerCartridgeMesh : type === 'ide' ? this.ideCartridgeMesh : this.fpsCartridgeMesh;
    const basePos = type === 'racer' ? this.racerCartridgeBasePos : type === 'ide' ? this.ideCartridgeBasePos : this.fpsCartridgeBasePos;

    sound.playCartridgeInsert();

    // Lift up, glide toward computer base, and return
    let progress = 0;
    const interval = setInterval(() => {
      progress += 0.08;
      if (progress <= 0.5) {
        // Lift and slide forward
        targetGroup.position.y = basePos.y + Math.sin(progress * Math.PI) * 0.25;
        targetGroup.position.z = basePos.z - progress * 0.6;
        targetGroup.rotation.x = -progress * 0.3;
      } else if (progress <= 1.0) {
        // Return to resting slot
        const t = (progress - 0.5) / 0.5;
        targetGroup.position.y = basePos.y + (1 - t) * 0.25;
        targetGroup.position.z = basePos.z - (1 - t) * 0.6;
        targetGroup.rotation.x = -(1 - t) * 0.3;
      } else {
        targetGroup.position.copy(basePos);
        targetGroup.rotation.set(0, 0, 0);
        clearInterval(interval);
      }
    }, 25);
  }

  public update(deltaTime: number) {
    this.time += deltaTime;

    // Spin fans
    this.fans.forEach((fan) => {
      fan.rotation.z += deltaTime * 8;
    });

    // Pulse cooling fluid
    const pulse = 0.5 + Math.sin(this.time * 5) * 0.5;
    this.coolingFluid.forEach((tube) => {
      (tube.material as THREE.MeshBasicMaterial).color.setHSL(0.5 + pulse * 0.05, 1, 0.5 + pulse * 0.3);
    });

    // Update Left Aux Screen (Matrix Rain)
    this.updateMatrixScreen(deltaTime);

    // Update Right Aux Screen (Telemetry Graph)
    this.updateTelemetryScreen(deltaTime);
  }

  private matrixDrops: number[] = new Array(16).fill(0);
  private updateMatrixScreen(_deltaTime: number) {
    const ctx = this.leftAuxCtx;
    const w = this.leftAuxCanvas.width;
    const h = this.leftAuxCanvas.height;

    ctx.fillStyle = 'rgba(6, 10, 16, 0.25)';
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = '#00fff2';
    ctx.font = '14px monospace';

    for (let i = 0; i < this.matrixDrops.length; i++) {
      const char = String.fromCharCode(0x30a0 + Math.floor(Math.random() * 96));
      const x = i * 16;
      const y = this.matrixDrops[i] * 16;

      ctx.fillText(char, x, y);

      if (y > h && Math.random() > 0.975) {
        this.matrixDrops[i] = 0;
      } else {
        this.matrixDrops[i]++;
      }
    }
    this.leftAuxTexture.needsUpdate = true;
  }

  private updateTelemetryScreen(deltaTime: number) {
    const ctx = this.rightAuxCtx;
    const w = this.rightAuxCanvas.width;
    const h = this.rightAuxCanvas.height;

    ctx.fillStyle = 'rgba(8, 12, 20, 0.2)';
    ctx.fillRect(0, 0, w, h);

    // Telemetry header
    ctx.fillStyle = '#ff007f';
    ctx.font = 'bold 12px monospace';
    ctx.fillText('NODE: TOILLE-UK', 12, 24);
    ctx.fillStyle = '#39ff14';
    ctx.fillText('ONLINE [200 OK]', 12, 42);

    // Sine waveform
    ctx.strokeStyle = '#00fff2';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 0; x < w; x += 4) {
      const y = 200 + Math.sin(x * 0.05 + this.time * 6) * 45;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Radar circle
    const cx = w / 2;
    const cy = 380;
    ctx.strokeStyle = 'rgba(255, 0, 127, 0.4)';
    ctx.beginPath();
    ctx.arc(cx, cy, 70, 0, Math.PI * 2);
    ctx.stroke();

    // Radar sweep hand
    const angle = this.time * 3;
    ctx.strokeStyle = '#ff007f';
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * 70, cy + Math.sin(angle) * 70);
    ctx.stroke();

    this.rightAuxTexture.needsUpdate = true;
  }
}
